import React, { useCallback, useEffect, useRef, useState } from "react";
import "./ConnectionStatus.css";

const CHECK_INTERVAL_MS = 20000;
const CHECK_TIMEOUT_MS = 5000;
const CONNECTION_STATUS_KEY = "hrms_connection_status";
const GIF_FAILED_KEY = "hrms_connection_gif_failed";

const readStorageValue = (key, fallback) => {
  try {
    if (typeof window === "undefined") return fallback;
    const value = window.localStorage.getItem(key);
    return value === null ? fallback : value;
  } catch (error) {
    return fallback;
  }
};

export default function ConnectionStatus() {
  const [status, setStatus] = useState(() => {
    const savedStatus = readStorageValue(CONNECTION_STATUS_KEY, "online");
    return savedStatus === "offline" || navigator.onLine === false ? "offline" : "online";
  });
  const [isChecking, setIsChecking] = useState(false);
  const [gifFailed, setGifFailed] = useState(() => readStorageValue(GIF_FAILED_KEY, "false") === "true");
  const timerRef = useRef(null);

  const persistStatus = useCallback((nextStatus) => {
    setStatus(nextStatus);
    try {
      window.localStorage.setItem(CONNECTION_STATUS_KEY, nextStatus);
    } catch (error) {
      // Ignore storage failures so UI still works in restricted browsers.
    }
  }, []);

  const checkConnection = useCallback(async () => {
    if (navigator.onLine === false) {
      persistStatus("offline");
      return false;
    }

    setIsChecking(true);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);

    try {
      await fetch(`${window.location.origin}/favicon.ico?connection_check=${Date.now()}`, {
        method: "GET",
        cache: "no-store",
        signal: controller.signal,
      });
      persistStatus("online");
      return true;
    } catch (error) {
      persistStatus("offline");
      return false;
    } finally {
      window.clearTimeout(timeout);
      setIsChecking(false);
    }
  }, [persistStatus]);

  useEffect(() => {
    try {
      window.localStorage.setItem(GIF_FAILED_KEY, String(gifFailed));
    } catch (error) {
      // Ignore storage failures so UI still works in restricted browsers.
    }
  }, [gifFailed]);

  useEffect(() => {
    const handleOnline = () => checkConnection();
    const handleOffline = () => persistStatus("offline");

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    checkConnection();

    timerRef.current = window.setInterval(checkConnection, CHECK_INTERVAL_MS);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.clearInterval(timerRef.current);
    };
  }, [checkConnection, persistStatus]);

  if (status === "offline") {
    return (
      <div className="connection-status-overlay" role="alert" aria-live="assertive">
        <div className="connection-status-card connection-status-card-offline">
          <div className="connection-status-orbit" aria-hidden="true"><span /><span /><span /></div>
          <div className="connection-status-icon">
            {gifFailed ? (
              <i className={`fa-solid fa-satellite-dish ${isChecking ? "fa-spin" : "connection-status-fallback-scan"}`} aria-hidden="true" />
            ) : (
              <img
                src={`${process.env.PUBLIC_URL}/starlink-star-link.gif`}
                alt="Connection"
                onError={() => {
                  setGifFailed(true);
                }}
              />
            )}
          </div>
          <span className="connection-status-kicker">Connection status</span>
          <h1>Looks like you are offline</h1>
          <p>It seems there&apos;s a problem with your connection. Please check your internet and try again.</p>
          <button type="button" className="connection-status-retry" onClick={checkConnection} disabled={isChecking}>
            <i className={`fa-solid ${isChecking ? "fa-spinner fa-spin" : "fa-rotate-right"}`} />
            {isChecking ? "Checking connection..." : "Try Again"}
          </button>
          <small className="connection-status-note">Your work will be available again as soon as the connection is restored.</small>
        </div>
      </div>
    );
  }

  return null;
}